/** @jsx isolateComponent */

import { Fragment } from 'react';

import { Divider, List } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Margin, Text } from '@zapier/design-system';

import {
  FeatureListing,
  PlanV2,
  Size,
  TooltipPosition,
} from '@zapier/design-system-revenue/src/types';

import { PlanFeature } from './PlanFeature';

type PlanFeatureListProps = {
  /** The features to show in the list. */
  features: Omit<FeatureListing, '__typename'>[];
  /** The plan to list the features of. */
  plan: PlanV2;
  /** The plan directly below the `plan` prop. For example, if `plan` is Starter, `previousPlan` is Free. If `plan` is Free, `previousPlan` is undefined. */
  previousPlan?: PlanV2;
  /** Renders a divider between the Zap Limit/Update Time features and the rest of the features if true. */
  showDivider?: boolean;
  /** Hides tooltips if false. */
  showTooltips?: boolean;
  /** The size of the screen. */
  size: Size;
  /** The title shown at the top off the feature list */
  title?: string;
  /** The direction in which the feature tooltips should appear. */
  tooltipPosition?: TooltipPosition;
};

const getZapLimit = (plan: PlanV2) =>
  plan.zaps === -1 ? 'Unlimited' : plan.zaps;

const isNewFeature = (feature: FeatureListing, previousPlan?: PlanV2 | null) =>
  Boolean(
    previousPlan &&
      previousPlan.features &&
      !previousPlan.features.find(f => f.name === feature.name)
  );

const isUpdateTimeDifferent = (plan: PlanV2, previousPlan?: PlanV2) =>
  previousPlan ? plan.zapsRunInterval !== previousPlan.zapsRunInterval : false;

const isZapCountDifferent = (plan: PlanV2, previousPlan?: PlanV2) =>
  previousPlan ? getZapLimit(plan) !== getZapLimit(previousPlan) : false;

/**
 * A list of plan features.
 */
export const PlanFeatureList = ({
  features,
  plan,
  previousPlan,
  showDivider = true,
  showTooltips = true,
  size,
  title,
  tooltipPosition,
}: PlanFeatureListProps) => {
  const volumeFeatures = [
    {
      href: null,
      name: `${getZapLimit(plan)} Zaps`,
      tooltip:
        'Zaps connect your apps and services together to automate repetitive tasks and save you time.',
    },
    {
      href: null,
      name: `${plan.zapsRunInterval} min Update Time`,
      tooltip: 'How often Zapier checks for new data to start your Zap.',
    },
  ];
  return (
    <Fragment>
      {title && (
        <Margin margin="0 0 20px 0">
          <Text color="black" tag="h3" type="allCaps800">
            {title}
          </Text>
        </Margin>
      )}

      <List
        getKey={(feature: FeatureListing, i: number) => `${feature.name}_${i}`}
        itemRenderer={(feature: FeatureListing, index) => (
          <PlanFeature
            feature={{
              href: feature.href,
              name: feature.name,
              tooltip: showTooltips ? feature.tooltip : null,
            }}
            iconColor="grey500"
            iconName={index === 0 ? 'miscBolt' : 'miscClock'}
            isEmphasized={
              index === 0
                ? isZapCountDifferent(plan, previousPlan)
                : isUpdateTimeDifferent(plan, previousPlan)
            }
            size={size}
            tooltipPosition={tooltipPosition}
          />
        )}
        items={volumeFeatures}
      />

      {showDivider && (
        <Margin margin="0 0 15px 35px">
          <Divider />
        </Margin>
      )}

      <List
        getKey={(feature: FeatureListing, i: number) => `${feature.name}_${i}`}
        itemRenderer={(feature: FeatureListing) => (
          <PlanFeature
            feature={feature}
            isEmphasized={isNewFeature(feature, previousPlan)}
            size={size}
            tooltipPosition={tooltipPosition}
          />
        )}
        items={features || []}
      />
    </Fragment>
  );
};
